import { useCallback, useEffect, useMemo, useState } from "react";
import { adminStorage } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Spinner from "../../components/ui/Spinner";
import { useAdminToast } from "../../context/AdminToastContext";

// Seller types are fully DB/admin driven. Each entry carries behavioural
// attributes consumed across the platform:
//   • allowed_groups    → which deal groups this seller may publish (validator)
//   • lead_components    → which AI leads route to this seller (RAG routing)
//   • search_namespaces  → own-market search gating (RAG pipeline)
// There are NO hardcoded seller types anywhere in code — this screen is the
// single source of truth (persisted to onboarding_options/seller_types).

interface SellerTypeRow {
  value: string;
  label: string;
  description: string;
  allowed_groups: string[];
  lead_components: string[];
  search_namespaces: string[];
  is_active: boolean;
  sort_order: number;
}

const EMPTY_ROW = (sort_order: number): SellerTypeRow => ({
  value: "",
  label: "",
  description: "",
  allowed_groups: [],
  lead_components: [],
  search_namespaces: [],
  is_active: true,
  sort_order,
});

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function slugify(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/^_+|_+$/g, "");
}

function normalizeRow(raw: any, idx: number): SellerTypeRow {
  const asList = (v: any): string[] =>
    Array.isArray(v)
      ? v.map((x) => String(x).trim()).filter(Boolean)
      : typeof v === "string" && v.trim()
        ? v.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
  if (typeof raw === "string") return { ...EMPTY_ROW(idx), value: raw, label: raw };
  return {
    value: String(raw.value ?? raw.label ?? "").trim(),
    label: String(raw.label ?? raw.value ?? "").trim(),
    description: String(raw.description ?? ""),
    allowed_groups: asList(raw.allowed_groups),
    lead_components: asList(raw.lead_components),
    search_namespaces: asList(raw.search_namespaces),
    is_active: raw.is_active !== false,
    sort_order: Number.isFinite(Number(raw.sort_order)) ? Number(raw.sort_order) : idx,
  };
}

// ── Chip multiselect (free-add): type a value, Enter adds; click × removes.
function ChipField({
  label,
  values,
  suggestions,
  onChange,
}: {
  label: string;
  values: string[];
  suggestions: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const add = (v: string) => {
    const t = v.trim();
    if (t && !values.includes(t)) onChange([...values, t]);
    setDraft("");
  };
  const remaining = suggestions.filter((s) => !values.includes(s));
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {values.length === 0 && <span className="text-[11px] text-gray-600 italic">none</span>}
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-900/40 border border-indigo-700/50 rounded-lg text-[11px] text-indigo-200">
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="text-indigo-400 hover:text-white">×</button>
          </span>
        ))}
      </div>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft); } }}
        placeholder="type + Enter to add"
        className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
      />
      {remaining.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {remaining.map((s) => (
            <button key={s} type="button" onClick={() => add(s)}
              className="px-1.5 py-0.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded text-[10px] text-gray-400">
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminSellerTypesPage() {
  const [rows, setRows] = useState<SellerTypeRow[]>([]);
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [opts, cats] = await Promise.all([
        apiClient.get<{ type: string; items: any[] }>("/api/v1/onboarding/options/seller_types", { headers: adminHeaders() }).then((r) => r.data).catch(() => null),
        apiClient.get<any[]>("/api/v2/categories").then((r) => r.data).catch(() => []),
      ]);
      const items = (opts?.items ?? []).map((it, i) => normalizeRow(it, i));
      setRows(items.sort((a, b) => a.sort_order - b.sort_order));

      const groups = new Set<string>();
      (Array.isArray(cats) ? cats : []).forEach((c: any) => {
        const g = (c?.group_slug ?? "").toString().trim();
        if (g) groups.add(g);
      });
      items.forEach((r) => r.allowed_groups.forEach((g) => groups.add(g)));
      setGroupOptions([...groups].sort());
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Suggestions for chip fields = union of all values already in use.
  const componentSuggestions = useMemo(
    () => [...new Set(rows.flatMap((r) => r.lead_components))].sort(),
    [rows],
  );
  const namespaceSuggestions = useMemo(
    () => [...new Set([...rows.flatMap((r) => r.search_namespaces), ...groupOptions])].sort(),
    [rows, groupOptions],
  );

  const patchRow = (idx: number, patch: Partial<SellerTypeRow>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const toggleGroup = (idx: number, group: string) =>
    setRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const has = r.allowed_groups.includes(group);
      return { ...r, allowed_groups: has ? r.allowed_groups.filter((g) => g !== group) : [...r.allowed_groups, group] };
    }));

  const addRow = () => setRows((prev) => [...prev, EMPTY_ROW(prev.length)]);

  const handleSave = async () => {
    setError(null);
    const cleaned = rows
      .map((r, i) => ({ ...r, value: slugify(r.value || r.label), label: r.label.trim() || (r.value || "").trim(), sort_order: i }))
      .filter((r) => r.value);

    const dupes = cleaned.map((r) => r.value).filter((v, i, a) => a.indexOf(v) !== i);
    if (dupes.length) {
      setError(`Duplicate seller-type values: ${[...new Set(dupes)].join(", ")}`);
      return;
    }

    setSaving(true);
    try {
      await apiClient.put(
        "/api/v1/onboarding/options/seller_types",
        { type: "seller_types", items: cleaned },
        { headers: adminHeaders() },
      );
      setSuccess(`Saved ${cleaned.length} seller types.`);
      setTimeout(() => setSuccess(null), 3000);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-4">
          <p className="text-gray-500 text-sm max-w-3xl">
            Seller types and their behaviour are fully DB/admin driven. <span className="text-gray-400">Allowed groups</span> control
            what a seller may publish, <span className="text-gray-400">lead components</span> route AI leads, and{" "}
            <span className="text-gray-400">search namespaces</span> gate own-market search. Changes apply within ~5 minutes (cache TTL) — usually instantly.
          </p>
          <button onClick={() => void load()} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm text-gray-300 shrink-0 self-start sm:ml-4">
            Refresh
          </button>
        </div>

        {error && <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
        {success && <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

        {loading ? (
          <div className="px-5 py-8 text-center text-gray-500 text-sm"><Spinner /></div>
        ) : (
          <div className="space-y-4">
            {rows.map((row, idx) => (
              <div key={idx} className="bg-gray-900 rounded-2xl border border-gray-800 p-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Value (slug)</label>
                    <input value={row.value} onChange={(e) => patchRow(idx, { value: e.target.value })}
                      placeholder="e.g. airline_company"
                      className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Label</label>
                    <input value={row.label} onChange={(e) => patchRow(idx, { label: e.target.value })}
                      placeholder="e.g. Airline Company"
                      className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
                  </div>
                  <div className="flex items-end gap-3">
                    <label className="flex items-center gap-2 text-xs text-gray-300 mb-1.5">
                      <input type="checkbox" checked={row.is_active} onChange={(e) => patchRow(idx, { is_active: e.target.checked })} />
                      Active
                    </label>
                    <button type="button" onClick={() => setDeleteIdx(idx)}
                      className="ml-auto px-3 py-1.5 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-xs">
                      Remove
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Description</label>
                  <textarea rows={2} value={row.description} onChange={(e) => patchRow(idx, { description: e.target.value })}
                    className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
                </div>

                <div className="mb-3">
                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Allowed groups (publish permissions)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {groupOptions.length === 0 && <span className="text-[11px] text-gray-600 italic">No category groups found</span>}
                    {groupOptions.map((g) => {
                      const on = row.allowed_groups.includes(g);
                      return (
                        <button key={g} type="button" onClick={() => toggleGroup(idx, g)}
                          className={`px-2.5 py-1 rounded-lg text-[11px] border transition-colors ${
                            on ? "bg-indigo-600 border-indigo-500 text-white" : "bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700"
                          }`}>
                          {g}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ChipField label="Lead components (AI lead routing)" values={row.lead_components}
                    suggestions={componentSuggestions} onChange={(v) => patchRow(idx, { lead_components: v })} />
                  <ChipField label="Search namespaces (own-market gate)" values={row.search_namespaces}
                    suggestions={namespaceSuggestions} onChange={(v) => patchRow(idx, { search_namespaces: v })} />
                </div>
              </div>
            ))}

            <div className="flex gap-3">
              <button type="button" onClick={addRow}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm text-gray-300">
                + Add seller type
              </button>
              <button onClick={() => void handleSave()} disabled={saving}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium disabled:opacity-50">
                {saving ? "Saving…" : "Save all"}
              </button>
            </div>
          </div>
        )}
      </div>

      {deleteIdx !== null && (
        <ConfirmModal
          title="Remove Seller Type"
          description={`"${rows[deleteIdx]?.label || rows[deleteIdx]?.value}" will be removed on save. Sellers already assigned this type keep it until they re-edit their profile.`}
          items={["Removed from the seller-type dropdown", "No longer routes AI leads or grants publish permissions"]}
          confirmLabel="Remove"
          onConfirm={() => { setRows((prev) => prev.filter((_, i) => i !== deleteIdx)); setDeleteIdx(null); }}
          onCancel={() => setDeleteIdx(null)}
        />
      )}
    </>
  );
}
